import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import classNames from "classnames";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  StarIcon,
} from "@heroicons/react/24/solid";
import { useInViewReveal } from "@/hooks/useInViewReveal";

interface ClientReview {
  nameKey: string;
  quoteKey: string;
  stars: number;
}

const reviews: ClientReview[] = [
  {
    nameKey: "ClientsSay.Client1Name",
    quoteKey: "ClientsSay.Client1Quote",
    stars: 4,
  },
  {
    nameKey: "ClientsSay.Client2Name",
    quoteKey: "ClientsSay.Client2Quote",
    stars: 5,
  },
  {
    nameKey: "ClientsSay.Client3Name",
    quoteKey: "ClientsSay.Client3Quote",
    stars: 5,
  },
  {
    nameKey: "ClientsSay.Client4Name",
    quoteKey: "ClientsSay.Client4Quote",
    stars: 4,
  },
];

export const ClientsSaySection = () => {
  const [t] = useTranslation("homeowners");
  const { ref: sectionRef, hasRevealed } = useInViewReveal({ threshold: 0.15 });
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const updateScrollState = () => {
    if (!scrollRef.current) return;
    const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
    setCanScrollLeft(scrollLeft > 0);
    setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 1);
  };

  const scroll = (direction: "left" | "right") => {
    if (!scrollRef.current) return;
    const cardWidth = scrollRef.current.firstElementChild
      ? (scrollRef.current.firstElementChild as HTMLElement).offsetWidth + 24
      : 300;
    scrollRef.current.scrollBy({
      left: direction === "left" ? -cardWidth : cardWidth,
      behavior: "smooth",
    });
  };

  return (
    <section
      ref={sectionRef}
      className="w-full bg-[#f6f5f2] px-6 sm:px-12 lg:px-24 pb-20 lg:pb-32"
    >
      {/* Title */}
      <h3
        className={classNames(
          "font-minion text-homeowners-headline text-center leading-snug text-[clamp(1.5rem,0.8rem+3vw,3.75rem)] mb-12 lg:mb-16 transition-all duration-[280ms] [transition-timing-function:cubic-bezier(0.4,0,0.2,1)]",
          {
            "opacity-100 translate-y-0": hasRevealed,
            "opacity-0 translate-y-[12px]": !hasRevealed,
          }
        )}
      >
        {t("ClientsSay.Title")}
      </h3>

      {/* Carousel */}
      <div
        className={classNames(
          "relative flex items-center gap-4 lg:gap-6 transition-all duration-[280ms] [transition-timing-function:cubic-bezier(0.4,0,0.2,1)] delay-[100ms]",
          {
            "opacity-100 translate-y-0": hasRevealed,
            "opacity-0 translate-y-[12px]": !hasRevealed,
          }
        )}
      >
        {/* Left arrow */}
        <button
          onClick={() => scroll("left")}
          className={classNames(
            "hidden lg:flex flex-shrink-0 w-8 h-8 items-center justify-center transition-opacity",
            { "opacity-30 cursor-default": !canScrollLeft }
          )}
          disabled={!canScrollLeft}
          aria-label="Scroll left"
        >
          <ChevronLeftIcon className="w-8 h-8 text-[#1a1a1a]" />
        </button>

        {/* Cards row */}
        <div
          ref={scrollRef}
          onScroll={updateScrollState}
          className="flex gap-6 overflow-x-auto scrollbar-hide snap-x snap-mandatory scroll-smooth"
        >
          {reviews.map((review) => (
            <div
              key={review.nameKey}
              className="bg-[#e3f1ea] rounded-[32px] p-6 lg:p-8 flex flex-col justify-between gap-6 min-w-[280px] sm:min-w-[320px] lg:min-w-0 lg:flex-1 snap-start"
            >
              <div className="flex flex-col gap-5 lg:gap-6">
                <p className="font-montserrat font-semibold text-[#1a1a1a] text-[clamp(0.75rem,0.65rem+0.35vw,1rem)]">
                  {t(review.nameKey)}
                </p>
                <hr className="border-[#b7b7b7]" />
                <p className="font-montserrat font-normal italic text-[#1a1a1a] leading-snug text-[clamp(0.75rem,0.65rem+0.35vw,1rem)] tracking-tight">
                  {t(review.quoteKey)}
                </p>
              </div>
              <div className="flex gap-2">
                {Array.from({ length: review.stars }).map((_, i) => (
                  <StarIcon
                    key={i}
                    className="w-6 h-6 lg:w-8 lg:h-8 text-purple-500"
                  />
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Right arrow */}
        <button
          onClick={() => scroll("right")}
          className={classNames(
            "hidden lg:flex flex-shrink-0 w-8 h-8 items-center justify-center transition-opacity",
            { "opacity-30 cursor-default": !canScrollRight }
          )}
          disabled={!canScrollRight}
          aria-label="Scroll right"
        >
          <ChevronRightIcon className="w-8 h-8 text-[#1a1a1a]" />
        </button>
      </div>
    </section>
  );
};
